import { useEffect, useState } from 'react'
import { useAppSelector, useAppDispatch, runnersActions, legsActions } from '../store/index'
import { AppShell } from '../components/layout/AppShell'
import { ElevationChart } from '../components/ElevationChart'
import { runnerService } from '../services/runnerService'
import { legService } from '../services/legService'
import { formatPace } from '../utils/paceCalc'
import type { Runner, LegAssignment } from '../types/index'

export function ProfilePage() {
  const dispatch = useAppDispatch()
  const loggedInRunnerId = useAppSelector(state => state.ui.loggedInRunnerId)
  const allLegs = useAppSelector(state => state.legs.assignments)

  const [runner, setRunner] = useState<Runner | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const [elevationLeg, setElevationLeg] = useState<number | null>(null)

  // Editable fields
  const [expectedPace, setExpectedPace] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [vanExchangeAlert, setVanExchangeAlert] = useState(false)

  useEffect(() => {
    if (!loggedInRunnerId) return
    const load = async () => {
      try {
        const [r, legs] = await Promise.all([
          runnerService.getRunner(loggedInRunnerId),
          legService.getLegs(),
        ])
        setRunner(r)
        setExpectedPace(r.expectedPace ? String(r.expectedPace) : '')
        setPhoneNumber(r.phoneNumber ?? '')
        setVanExchangeAlert(r.vanExchangeAlert ?? false)
        dispatch(runnersActions.setRunners([r]))
        dispatch(legsActions.setLegAssignments(legs))
      } catch (err) {
        console.error('Failed to load profile:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [loggedInRunnerId, dispatch])

  const assignedLegs: LegAssignment[] = allLegs
    .filter(l => l.runnerId === loggedInRunnerId)
    .sort((a, b) => a.legNumber - b.legNumber)

  const handleSave = async () => {
    if (!runner) return
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      const paceVal = expectedPace ? parseFloat(expectedPace) : null
      const updated = await runnerService.updateRunnerProfile(runner.id, {
        expectedPace: paceVal,
        phoneNumber: phoneNumber.trim() || null,
        vanExchangeAlert,
      })
      setRunner(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err: any) {
      setSaveError(err?.response?.data?.error ?? 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Loading profile…</div>
      </AppShell>
    )
  }

  if (!runner) {
    return (
      <AppShell>
        <div className="p-4 text-sm text-red-500">Runner profile not found.</div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="h-full overflow-y-auto p-4 space-y-4 max-w-md mx-auto pb-24">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          My Profile
        </h1>

        {/* Read-only info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Runner Info</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Name</p>
              <p className="font-semibold">{runner.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Van</p>
              <p className="font-semibold">Van {runner.vanNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Runner #</p>
              <p className="font-semibold">#{runner.runnerNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Avg Pace</p>
              <p className="font-semibold font-mono">
                {runner.averagePace ? formatPace(runner.averagePace) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Van Exchange Alert */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Van Exchange Alert</h2>
          <p className="text-sm font-medium">Text Message Notifications</p>
          <p className="text-xs text-gray-400 mt-0.5">Text me when the other van starts their last leg</p>
          <p className="text-sm font-bold text-gray-700 dark:text-gray-200 leading-relaxed bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
            I agree to receive text messages from TeziLucas LLC by selecting the Opt-In button.
          </p>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vanExchangeAlert"
                checked={vanExchangeAlert}
                onChange={() => setVanExchangeAlert(true)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Opt-In</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vanExchangeAlert"
                checked={!vanExchangeAlert}
                onChange={() => setVanExchangeAlert(false)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Opt-Out</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cell Phone</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              placeholder="e.g. 5551234567"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
            />
            <p className="text-xs text-gray-400 mt-1">10-digit US number, no dashes or spaces</p>
          </div>
        </div>

        {/* Assigned legs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Assigned Legs</h2>
          </div>
          {assignedLegs.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400">No legs assigned yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-1.5 text-left font-semibold text-gray-600 dark:text-gray-400">Leg</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-gray-600 dark:text-gray-400">Start</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-gray-600 dark:text-gray-400">End</th>
                  <th className="px-3 py-1.5 text-center font-semibold text-gray-600 dark:text-gray-400">Miles</th>
                  <th className="px-3 py-1.5 text-center font-semibold text-gray-600 dark:text-gray-400">Diff</th>
                  <th className="px-3 py-1.5 text-center font-semibold text-gray-600 dark:text-gray-400">Elev</th>
                </tr>
              </thead>
              <tbody>
                {assignedLegs.map(leg => (
                  <>
                    <tr key={leg.id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-1.5 font-bold">#{leg.legNumber}</td>
                      <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400 truncate max-w-[80px]">{leg.startAddress ?? '—'}</td>
                      <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400 truncate max-w-[80px]">{leg.endAddress ?? '—'}</td>
                      <td className="px-3 py-1.5 text-center font-mono">{leg.distanceMiles?.toFixed(1)}</td>
                      <td className="px-3 py-1.5 text-center">{leg.difficultyRating}</td>
                      <td className="px-3 py-1.5 text-center">
                        <button
                          onClick={() => setElevationLeg(leg.legNumber)}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                          title="View elevation profile"
                        >
                          <svg className="w-4 h-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l4-4 4 4 4-8 4 4V3" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {leg.vanTransfer && (
                      <tr key={`${leg.id}-vantransfer`} className="border-t border-gray-50 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/20">
                        <td colSpan={6} className="px-3 py-1.5">
                          <p className="text-xs text-purple-800 dark:text-purple-300 flex items-center gap-1.5">
                            <span>🚐</span>
                            <span className="font-semibold">Van transfer after this leg</span>
                            <span className="text-purple-500">— all runners travel by van to the next start</span>
                          </p>
                        </td>
                      </tr>
                    )}
                    {leg.instructions && (
                      <tr key={`${leg.id}-instructions`} className="border-t border-gray-50 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
                        <td colSpan={6} className="px-3 py-1.5">
                          <p className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-1.5">
                            <span className="flex-shrink-0 mt-px">⚠</span>
                            <span>{leg.instructions}</span>
                          </p>
                        </td>
                      </tr>
                    )}
                    {leg.xpInstructions && (
                      <tr key={`${leg.id}-xp-instructions`} className="border-t border-gray-50 dark:border-gray-700 bg-teal-50 dark:bg-teal-900/20">
                        <td colSpan={6} className="px-3 py-1.5">
                          <p className="text-xs text-teal-800 dark:text-teal-300 flex items-start gap-1.5">
                            <span className="flex-shrink-0 mt-px">ℹ️</span>
                            <span><span className="font-semibold">XP {leg.legNumber}:</span> {leg.xpInstructions}</span>
                          </p>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Editable settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 space-y-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">My Settings</h2>

          <div>
            <label className="block text-sm font-medium mb-1">Expected Pace (min/mile)</label>
            <input
              type="number"
              step="0.1"
              min="4"
              max="20"
              value={expectedPace}
              onChange={e => setExpectedPace(e.target.value)}
              placeholder="e.g. 8.5"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
            />
            <p className="text-xs text-gray-400 mt-1">Used for ETA calculations</p>
          </div>

        </div>

        {/* Save */}
        {saveError && (
          <p className="text-sm text-red-600">{saveError}</p>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : saved ? (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              Saved!
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      {elevationLeg !== null && (
        <ElevationChart legNumber={elevationLeg} onClose={() => setElevationLeg(null)} />
      )}
    </AppShell>
  )
}
